// DepartmentsSection.jsx
import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from './ui/card';
import { Building2, ArrowRight } from 'lucide-react';

export const DepartmentsSection = () => {
  const navigate = useNavigate();
  const deptRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('fade-in');
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = deptRef.current?.querySelectorAll('.dept-card');
    elements?.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const featuredDepartments = [
    {
      name: 'Emergency Department',
      image: '/images/department/emergency.jpg',
      description: '24/7 emergency medical care with rapid response teams for critical situations.',
    },
    {
      name: 'Orthopedics',
      image: '/images/department/orthopedic.jpg',
      description: 'Advanced treatment for bone, joint, and musculoskeletal conditions.',
    },
    {
      name: 'Pediatrics',
      image: '/images/department/Pediatric.jpg',
      description: 'Specialized healthcare for infants, children, and adolescents.',
    },
    {
      name: 'Obstetrics & Gynecology',
      image: '/images/department/Obstetrics.jpg',
      description: 'Comprehensive women healthcare services and maternity care.',
    },
  ];

  return (
    <section id="departments" className="py-24 bg-[hsl(var(--background))]" ref={deptRef}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16 space-y-4">
          <div className="inline-flex items-center space-x-2 bg-[hsl(var(--primary))]/10 px-4 py-2 rounded-full">
            <Building2 className="w-5 h-5 text-[hsl(var(--primary))]" />
            <span className="text-sm font-semibold text-[hsl(var(--primary))]">
              Our Departments
            </span>
          </div>
          <h2 className="text-4xl sm:text-5xl font-bold text-[hsl(var(--foreground))]">
            Specialized Medical
            <span className="block mt-2 text-gradient">Departments</span>
          </h2>
          <p className="text-lg text-[hsl(var(--muted-foreground))] max-w-2xl mx-auto">
            Our specialized medical departments work together to provide comprehensive,
            coordinated care for all your health needs.
          </p>
        </div>

        {/* Featured Departments Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
          {featuredDepartments.map((dept, index) => (
            <div
              key={index}
              className="dept-card opacity-0"
              style={{ transitionDelay: `${index * 150}ms` }}
            >
              <Card className="group overflow-hidden hover:shadow-[var(--shadow-xl)] transition-all duration-500 border-none h-full">
                <div className="relative h-64 overflow-hidden">
                  <img
                    src={dept.image}
                    alt={dept.name}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    onError={(e) => {
                      e.target.src = 'https://images.unsplash.com/photo-1559757175-0eb30cd8c063?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80';
                    }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[hsl(var(--foreground))]/80 via-[hsl(var(--foreground))]/40 to-transparent"></div>
                  <div className="absolute bottom-0 left-0 right-0 p-6">
                    <h3 className="text-2xl font-bold text-white mb-2">
                      {dept.name}
                    </h3>
                    <p className="text-white/90 text-sm">{dept.description}</p>
                  </div>
                </div>
              </Card>
            </div>
          ))}
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-12">
          <div className="text-center p-4">
            <div className="text-3xl font-bold text-[hsl(var(--primary))]">16+</div>
            <div className="text-sm text-[hsl(var(--muted-foreground))]">Departments</div>
          </div>
          <div className="text-center p-4">
            <div className="text-3xl font-bold text-[hsl(var(--primary))]">50+</div>
            <div className="text-sm text-[hsl(var(--muted-foreground))]">Expert Doctors</div>
          </div>
          <div className="text-center p-4">
            <div className="text-3xl font-bold text-[hsl(var(--primary))]">24/7</div>
            <div className="text-sm text-[hsl(var(--muted-foreground))]">Emergency</div>
          </div>
          <div className="text-center p-4">
            <div className="text-3xl font-bold text-[hsl(var(--primary))]">100%</div>
            <div className="text-sm text-[hsl(var(--muted-foreground))]">Quality Care</div>
          </div>
        </div>

        {/* See More Button */}
        <div className="text-center">
          <button
            onClick={() => navigate('/departments')}
            className="bg-[hsl(var(--primary))] text-white px-8 py-3 rounded-full font-semibold hover:bg-[hsl(var(--primary-dark))] transition-colors inline-flex items-center space-x-2 group"
          >
            <span>View All Departments</span>
            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </button>
        </div>
      </div>
    </section>
  );
};